<template>
  <ViewContainer :title="$t('menu.admin.admin_people')">
    <v-container fill-height class="ma-0 pa-0 mb-4">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <v-card class="px-8 py-6">
            <p
              data-testid="addPeopleBulk-text-title"
              class="pa-0 mb-3 headline"
            >
              {{ $t('common.users.add_users_bulk_title') }}
            </p>
            <p
              class="pa-0 ma-0 mb-6 blue-grey--text text--darken-1 body-2"
              v-html="
                $t('common.users.add_users_bulk_subtitle', {
                  href: validHref,
                  helpCenterLink:
                    'https://help.reverscore.net/en/article/manage-users-as-a-site-admin-1sdv2dz/',
                })
              "
              @click="handleSubtitleClick"
            />
            <v-card-text class="ma-0 pa-0">
              <v-form v-model="isFormValid" ref="addBulkUsersForm">
                <v-row class="ma-0 pa-0">
                  <v-col
                    cols="12"
                    sm="12"
                    md="3"
                    lg="3"
                    xl="3"
                    class="ma-0 pa-0 pe-6"
                  >
                    <p
                      class="ma-0 pa-0 text-body-1 font-weight-bold blue-grey--text text--darken-3"
                    >
                      {{ $t('people.set_up') }}
                    </p>
                    <LocationSelector
                      verticalLayout
                      :organizationId="organizationToUseId"
                      :location="form.location"
                      @change="locationSelectorChange"
                    />

                    <TeamSelector
                      :organizationId="organizationToUseId"
                      :siteId="form.location.site"
                      :selectedTeamId="form.selectedTeam"
                      @change="form.selectedTeam = $event"
                      @click.native="
                        trackAddPeopleNavigate({
                          element: 'teamsSelector',
                          section: 'setup',
                        })
                      "
                    />
                    <v-row
                      class="ma-0 pa-0 mt-6"
                      v-if="organizationAuthIsLocal"
                    >
                      <v-col cols="12" class="ma-0 pa-0 d-flex align-center">
                        <p
                          class="ma-0 pa-0 text-body-1 font-weight-bold blue-grey--text text--darken-3"
                        >
                          {{ $t('common.temporary_password') }}
                        </p>
                        <v-tooltip top max-width="284px">
                          <template v-slot:activator="{ on }">
                            <v-icon
                              v-on="on"
                              class="ms-2"
                              size="20px"
                              style="margin-top: 1px !important"
                            >
                              mdi-help-circle-outline
                            </v-icon>
                          </template>
                          {{ $t('common.temporary_password_description') }}
                        </v-tooltip>
                      </v-col>
                    </v-row>
                    <AddPassword
                      v-if="organizationAuthIsLocal"
                      class="mt-4"
                      :password="form.password"
                      :confirmPassword="form.confirmPassword"
                      :isLoading="isSaving"
                      @change="handlePasswordChange"
                    />
                  </v-col>
                  <v-col
                    sm="12"
                    md="9"
                    lg="9"
                    xl="9"
                    cols="12"
                    class="ma-0 pa-0"
                  >
                    <div class="d-flex justify-space-between mb-4">
                      <p
                        class="ma-0 pa-0 text-body-1 font-weight-bold blue-grey--text text--darken-3"
                      >
                        {{ $t('common.users.user_other') }}
                      </p>
                      <a
                        class="text-link body3 ma-0 pa-0"
                        data-testId="addBulkUsers-link-downloadTemplate"
                        style="text-decoration: none"
                        @click="
                          trackAddPeopleNavigate({
                            element: 'dowloadTemplate',
                            section: 'button',
                          })
                        "
                        :href="validHref"
                      >
                        <v-icon small color="blue">mdi-tray-arrow-down</v-icon>
                        {{ $t('common.users.download_template') }}
                      </a>
                    </div>
                    <FileLoader
                      v-if="currentStep === STEPS.selectCSVFile"
                      @fileSelected="parseFile"
                    />
                    <UsersTable
                      v-if="currentStep === STEPS.usersView"
                      :canAssignRole="canAssignRole"
                      :defaultSiteId="form.location.site"
                      :isSaving="isSaving"
                      :rolesEnabled="rolesEnabled"
                      :userList="form.usersToInvite"
                      @onChange="onChange"
                      @removeUser="removeUser"
                    />
                    <Summary
                      v-if="currentStep === STEPS.summaryView"
                      :created="totalAccountsCreated"
                      :missed="totalAccountsMissed"
                      @addMore="handleAddMore"
                      @viewAccounts="handleViewAccounts"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions class="justify-end" v-if="hasUserList">
              <Button
                plain
                color="grey"
                data-testid="addUsersBulk-button-cancel"
                :text="$t('common.button.cancel')"
                @click="handleCancel"
              />
              <Button
                color="primary"
                data-testid="addUsersBulk-button-save"
                :disabled="isSaving"
                :text="$t('common.button.add')"
                :loading="isSaving"
                @click="handleSend"
              />
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <DeleteConfirmationModal
      :maxWidth="372"
      :show="showCancelModal"
      :confirmText="$t('common.button.discard')"
      :title="$t('common.users.cancel_bulk_operation')"
      :text="$t('common.users.cancel_bulk_operation_text')"
      @cancel="handleCancelLeaveRoute"
      @delete="handleDiscard"
    />
    <MissedAccountsModal
      :show="showSummaryModal"
      :missedAccounts="missedAccounts"
      @close="showSummaryModal = false"
    />
  </ViewContainer>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import access from '@/lib/access.js'
import _ from 'lodash'
import Papa from 'papaparse'

import validationHelper from '@/lib/validationHelper.js'
import api from '@/api'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import LocationSelector from '@/components/LocationSelector/LocationSelector.vue'
import AddPassword from '@/components/AddPassword.vue'
import TeamSelector from '@/components/People/AddPeopleModal/TeamSelectorAddBulk.vue'
import FileLoader from './FileLoader.vue'
import UsersTable from './UsersTable.vue'
import Button from '@/components/UI/Button'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import MissedAccountsModal from './MissedAccountsModal.vue'
import Summary from './Summary.vue'

const STEPS = {
  selectCSVFile: 'selectFile',
  usersView: 'usersView',
  summaryView: 'summaryView',
}

export default {
  name: 'AddUsersBulk',

  mixins: [mixin, trackerMixin],

  components: {
    ViewContainer,
    LocationSelector,
    TeamSelector,
    FileLoader,
    UsersTable,
    Button,
    DeleteConfirmationModal,
    MissedAccountsModal,
    Summary,
    AddPassword,
  },

  props: {
    organizationId: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      form: {
        location: {
          organization: '',
          site: '',

          tags: [],
        },
        password: '',
        confirmPassword: '',
        selectedTeam: '',
        usersToInvite: [],
      },
      isFormValid: false,
      isLoading: false,
      STEPS,
      currentStep: STEPS.selectCSVFile,
      showCancelModal: false,
      isSaving: false,
      missedAccounts: [],
      totalAccountsCreated: 0,
      showSummaryModal: false,
      formChanged: false,
      originalFormData: {
        location: {
          organization: '',
          site: '',
          tags: [],
        },
        password: '',
        confirmPassword: '',
        selectedTeam: '',
        usersToInvite: [],
      },
      organization: {},
    }
  },

  async mounted() {
    await this.getOrganization()

    if (!this.isOwnOrganization) return

    this.form.location = this.accountLocation
    this.originalFormData.location = _.cloneDeep(this.accountLocation)
  },

  watch: {
    form: {
      deep: true,
      immediate: false,
      handler(newValue, oldValue) {
        const hasLocation = Boolean(oldValue?.location?.organization?.length)
        const locationChanged = !_.isEqual(
          newValue.location,
          this.originalFormData.location
        )
        const formChanged = !_.isEqual(newValue, this.originalFormData)
        if (hasLocation && (locationChanged || formChanged)) {
          this.formChanged = true
        }
      },
    },
  },

  computed: {
    ...mapGetters(['account']),
    ...mapState({
      user: state => state.access.account,
    }),

    isOwnOrganization() {
      if (!this.organizationId) return true
      return this.organizationId === this.accountLocation.organization
    },

    organizationAuthIsLocal() {
      return this.organization?.authenticationType?.includes?.('local') ?? false
    },

    rolesEnabled() {
      if (access.isOrganizationAdmin(this.user)) {
        return [
          'isOrganizationAdmin',
          'isSiteAdmin',
          'isCoach',
          'canDoFinancialValidation',
          'isRoutineManager',
        ]
      }
      if (access.isLeadership(this.user)) {
        return ['isCoach', 'canDoFinancialValidation', 'isRoutineManager']
      }
      return []
    },

    accountLocation() {
      return {
        organization: this.account?.organization?._id,
        site: this.account?.site?._id,
        tags: [],
      }
    },

    hasUserList() {
      return !_.isEmpty(this.form.usersToInvite)
    },

    totalAccountsMissed() {
      return this.missedAccounts.length ?? 0
    },

    validHref() {
      return access.isOrganizationAdmin(this.user)
        ? '/files/rever-accounts-template-for-admins.csv'
        : this.canAssignRole
          ? '/files/rever-accounts-template.csv'
          : '/files/rever-accounts-template-for-coaches.csv'
    },

    canAssignRole() {
      return access.isLeadership(this.user)
    },

    organizationToUseId() {
      return this.organizationId ?? this.accountLocation.organization
    },
  },

  methods: {
    async getOrganization() {
      try {
        this.isLoading = true

        const response = await api.organization.getById(
          this.organizationToUseId
        )

        this.organization = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleCancel() {
      this.trackAddPeopleNavigate({
        element: 'cancelButton',
        section: 'addUsersStep',
      })
      this.showCancelModal = true
    },

    handleSubtitleClick(event) {
      if (event?.target?.id === 'download_template')
        return this.trackAddPeopleNavigate({
          element: 'dowloadTemplate',
          section: 'subtitle',
        })
      if (event?.target?.id === 'learn_more')
        return this.trackAddPeopleNavigate({
          element: 'learnMore',
          section: 'subtitle',
        })
    },

    getDomain(email) {
      return email?.replace?.(/.*@/, '') ?? ''
    },

    locationSelectorChange(newLocation) {
      this.form.location = {
        ...newLocation,
        organization: this.organizationToUseId,
      }
    },

    handlePasswordChange(password) {
      this.form = { ...this.form, ...password }
    },

    parseFile(files) {
      Papa.parse(files[0], {
        encoding: 'utf8',
        complete: results => {
          this.handleFileSelected(results)
        },
      })
    },

    handleFileSelected(parsedFile) {
      this.isLoading = true

      if (!_.isEmpty(parsedFile?.errors))
        return this.showError(this.$t('common.users.invalid_file_error'))

      this.form.usersToInvite = parsedFile.data
        .reduce((result, user, index) => {
          if (!user.every(item => item === '')) {
            result.push({
              displayname: user[0],
              familyname: user[1],
              position: user[2],
              email: user[3],
              isCoach: !_.isEmpty(user[4]),
              isRoutineManager: !_.isEmpty(user[5]),
              canDoFinancialValidation: !_.isEmpty(user[6]),
              isSiteAdmin: !_.isEmpty(user[7]),
              hasError: false,
              isDuplicated: false,
              originalIndex: index,
              isOrganizationAdmin: !_.isEmpty(user[8]),
            })
          }
          return result
        }, [])
        .slice(1)

      if (!this.form.usersToInvite.length)
        return this.showError(this.$t('common.users.no_users_were_found'))

      this.validateUsersTable()

      this.currentStep = STEPS.usersView

      this.$nextTick(() => {
        this.$refs.addBulkUsersForm.validate()
      })
    },

    hasStringError(name) {
      const trimmedName = name.trim()

      if (!trimmedName) {
        return true
      }
    },

    hasFirstNameError(name) {
      if (this.hasStringError(name)) {
        return true
      }
    },

    hasLastNameError(name) {
      if (this.hasStringError(name)) {
        return true
      }
    },

    hasEmailError(email) {
      if (
        this.hasStringError(email) ||
        !validationHelper.isValidEmailAddress(email) ||
        this.$constants.organization.domainsBlacklist.includes(
          this.getDomain(email)
        )
      ) {
        return true
      }
    },

    isDuplicated(email) {
      if (this.hasStringError(email)) {
        return
      }

      return (
        this.form.usersToInvite.filter(user => user.email === email).length >
        1
      )
    },

    hasUserErrors(user, index) {
      if (this.hasFirstNameError(user.displayname)) {
        this.form.usersToInvite[index].hasError = true
      }

      if (this.hasLastNameError(user.familyname)) {
        this.form.usersToInvite[index].hasError = true
      }

      if (this.hasEmailError(user.email)) {
        this.form.usersToInvite[index].hasError = true
      }

      if (this.isDuplicated(user.email)) {
        this.form.usersToInvite[index].hasError = true
        this.form.usersToInvite[index].isDuplicated = true
      }
    },

    validateUsersTable() {
      this.form.usersToInvite.forEach((user, index) => {
        this.hasUserErrors(user, index)
      })
    },

    resetValues() {
      this.form.usersToInvite = []
      this.currentStep = STEPS.selectCSVFile
      this.form.selectedTeam = ''
      this.form.password = ''
      this.form.confirmPassword = ''
      this.$refs.addBulkUsersForm.resetValidation()
      this.formChanged = false
    },

    onChange(data) {
      this.handleDuplicatedChange()

      this.form.usersToInvite = this.form.usersToInvite.map(user => {
        if (user.originalIndex === data.originalIndex) {
          return { ...user, ...data.value }
        }
        return user
      })
    },

    removeUser(originalIndex) {
      this.form.usersToInvite = this.form.usersToInvite.filter(
        user => user.originalIndex !== originalIndex
      )

      this.checkDuplication()

      this.$refs.addBulkUsersForm.validate()
    },

    async createAccounts() {
      this.isSaving = true
      const users = this.form.usersToInvite.map(user => {
        const payload = {
          ...user,
          organizationId: this.organizationToUseId,
          site: this.form.location.site,
          tags: this.form.location.tags,
          password: this.form.password,
          teams: _.isEmpty(this.form.selectedTeam)
            ? []
            : [this.form.selectedTeam],

          isRoutineManager: this.canAssignRole ? user.isRoutineManager : false,
          isCoach: this.canAssignRole ? user.isCoach : false,
          canDoFinancialValidation: this.canAssignRole
            ? user.canDoFinancialValidation
            : false,
          isSiteAdmin: this.rolesEnabled.includes('isOrganizationAdmin')
            ? user.isSiteAdmin
            : false,
          isOrganizationAdmin: this.rolesEnabled.includes('isOrganizationAdmin')
            ? user.isOrganizationAdmin
            : false,
        }

        return payload
      })

      try {
        const response = await api.bulkaccount.create({
          organizationId: this.organizationToUseId,
          accounts: users,
        })

        const hasErrors = response.data.some(account => account.hasError)

        if (hasErrors) {
          this.missedAccounts = response?.data?.filter(
            account => account.hasError
          )
        }

        this.totalAccountsCreated =
          response?.data?.filter(account => !account.hasError).length ?? 0

        if (this.totalAccountsCreated > 0) {
          this.showSuccess(this.$t('common.users.add_bulk_api_success'))
        }

        this.goToSummary()
      } catch (error) {
        this.showError(this.$t('common.users.add_bulk_api_error'))
      } finally {
        this.isSaving = false
      }
    },

    checkDuplication() {
      this.form.usersToInvite.forEach((user, index) => {
        if (this.isDuplicated(user.email)) {
          this.form.usersToInvite[index].isDuplicated = true
        } else {
          this.form.usersToInvite[index].isDuplicated = false
          if (this.hasEmailError(user.email)) {
            this.form.usersToInvite[index].hasError = true
          }
        }
      })
    },

    handleSend() {
      this.checkDuplication()

      if (this.form.usersToInvite.some(user => user.isDuplicated)) {
        return this.showError(this.$t('common.users.add_bulk_error_duplicated'))
      }

      this.$refs.addBulkUsersForm.validate()

      if (!this.isFormValid) {
        return this.showError(this.$t('common.users.add_bulk_error'))
      }

      this.trackAddPeopleNavigate({
        element: 'sendButton',
        section: 'addUsersStep',
      })

      this.createAccounts()
    },

    goToSummary() {
      this.resetValues()
      this.currentStep = STEPS.summaryView
    },

    handleAddMore() {
      this.missedAccounts = []
      this.totalAccountsCreated = 0
      this.currentStep = STEPS.selectCSVFile
      this.trackAddPeopleNavigate({
        element: 'addMore',
        section: 'summary',
      })
    },

    handleViewAccounts() {
      this.trackAddPeopleNavigate({
        element: 'viewDuplicatedAccountsButton',
        section: 'summary',
      })
      this.showSummaryModal = true
    },

    handleDuplicatedChange: _.debounce(function () {
      this.checkDuplication()
    }, 500),

    handleCancelLeaveRoute() {
      this.showCancelModal = false
      this.router = null
    },

    handleDiscard() {
      this.formChanged = false
      this.showCancelModal = false
      const route = this.router?.to ?? { name: 'people' }

      this.navigateTo(route)
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.formChanged) {
      this.showCancelModal = true
      this.router = { to, from, next }
      return next(false)
    } else {
      return next()
    }
  },
}
</script>
